import React, { useState, useRef, useEffect } from 'react';
import { 
  Mic,
  MicOff,
  Play,
  Pause,
  Square,
  Download,
  Trash2,
  RefreshCw,
  Search,
  Filter,
  Plus,
  Edit,
  Copy,
  Share,
  Bookmark,
  Star,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  Building,
  Calendar,
  Settings,
  Database,
  Layers,
  Lock,
  Key,
  Fingerprint,
  Globe,
  MapPin,
  Phone,
  Mail,
  User,
  Save,
  X,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Link,
  Zap,
  Languages,
  Users2,
  Calculator,
  Receipt,
  CreditCard,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Info,
  ExternalLink,
  Archive,
  Send,
  MessageSquare,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  AlertCircle,
  FileText,
  Eye,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  TrendingUp,
  TrendingDown,
  Target,
  Shield,
  AlertTriangle,
  BarChart3,
  PieChart,
  Brain
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

const VoiceToContract = () => {
  const { success, error: showError, info } = useToast();
  const [activeTab, setActiveTab] = useState('record');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [transcriptionText, setTranscriptionText] = useState('');
  const [generatedContract, setGeneratedContract] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [selectedContractType, setSelectedContractType] = useState('service-agreement');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Mock data for voice-to-contract sessions
  const [voiceSessions, setVoiceSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  // Filter sessions
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const filteredSessions = voiceSessions.filter(session => {
    const matchesSearch = searchTerm === '' || 
      session.sessionName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.contractType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || session.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Fetch voice sessions from backend
  const fetchVoiceSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await AIService.getVoiceSessions({ limit: 100 });
      if (response.success && response.data) {
        setVoiceSessions(response.data);
      }
    } catch (error) {
      console.error('Error fetching voice sessions:', error);
      showError('Failed to load voice sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  // Calculate statistics from real data
  const stats = {
    totalSessions: voiceSessions.length,
    completedSessions: voiceSessions.filter(s => s.status === 'Completed').length,
    processingSessions: voiceSessions.filter(s => s.status === 'Processing' || s.status === 'Transcribing' || s.status === 'Generating').length,
    errorSessions: voiceSessions.filter(s => s.status === 'Error').length,
    averageAccuracy: voiceSessions.filter(s => s.transcriptionAccuracy > 0).length > 0
      ? Math.round(voiceSessions.filter(s => s.transcriptionAccuracy > 0).reduce((sum, s) => sum + s.transcriptionAccuracy, 0) / voiceSessions.filter(s => s.transcriptionAccuracy > 0).length)
      : 0,
    totalDuration: voiceSessions.reduce((sum, s) => {
      try {
        const durationParts = s.duration?.split(':') || ['0', '0'];
        return sum + (parseInt(durationParts[0]) * 60 + parseInt(durationParts[1]));
      } catch (e) {
        return sum;
      }
    }, 0),
    contractsGenerated: voiceSessions.filter(s => s.contractGenerated).length
  };

  const tabs = [
    { id: 'record', label: 'Voice Recording' },
    { id: 'transcription', label: 'Transcription' },
    { id: 'contracts', label: 'Generated Contracts' },
    { id: 'sessions', label: 'Session History' },
    { id: 'analytics', label: 'Analytics' }
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'sessionName',
      label: 'Session',
      width: '300px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.contractType} • {row.duration} • {row.participants.length} participants
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status, row) => {
        const statusConfig = {
          'Completed': { color: '#10b981', bg: '#d1fae5', icon: <CheckCircle size={14} /> },
          'Processing': { color: '#f59e0b', bg: '#fef3c7', icon: <RefreshCw size={14} /> },
          'Error': { color: '#ef4444', bg: '#fee2e2', icon: <XCircle size={14} /> }
        };
        const config = statusConfig[status] || statusConfig['Processing'];
        
        return (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '0',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: config.bg,
            color: config.color,
            width: 'fit-content'
          }}>
            {config.icon}
            {status}
          </span>
        );
      }
    },
    {
      key: 'transcriptionAccuracy',
      label: 'Accuracy',
      width: '120px',
      render: (accuracy) => {
        if (accuracy === 0) return <span style={{ color: '#6b7280' }}>-</span>;
        
        const getAccuracyColor = (accuracy) => {
          if (accuracy >= 90) return '#10b981';
          if (accuracy >= 80) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${accuracy}%`,
                height: '100%',
                backgroundColor: getAccuracyColor(accuracy),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: getAccuracyColor(accuracy) }}>
              {accuracy}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'contractGenerated',
      label: 'Contract',
      width: '100px',
      render: (generated) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500',
          backgroundColor: generated ? '#d1fae5' : '#fee2e2',
          color: generated ? '#10b981' : '#ef4444'
        }}>
          {generated ? 'Generated' : 'Pending'}
        </span>
      )
    },
    {
      key: 'recordingDate',
      label: 'Recorded',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '200px',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={() => handlePlay(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '0',
              borderColor: 'var(--primary-color)',
              color: 'var(--primary-color)',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Play Recording"
          >
            <Play size={14} /> Play
          </button>
          <button
            onClick={() => handleView(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '0',
              borderColor: '#6b7280',
              color: '#6b7280',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="View Details"
          >
            <Eye size={14} /> View
          </button>
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: '6px 10px',
              fontSize: '12px',
              borderRadius: '0',
              borderColor: '#ef4444',
              color: '#ef4444',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Delete Session"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )
    }
  ];

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine best mime type
      const mimeTypes = ['audio/webm', 'audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
      let selectedMimeType = 'audio/webm';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: selectedMimeType 
        });
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        processAudio(audioBlob);
      };

      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start duration timer
      intervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      // Audio level monitoring
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateAudioLevel = () => {
        if (isRecording) {
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setAudioLevel(average);
          requestAnimationFrame(updateAudioLevel);
        } else {
          audioContext.close();
        }
      };
      updateAudioLevel();
    } catch (error) {
      console.error('Error starting recording:', error);
      showError('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(intervalRef.current);
      setAudioLevel(0);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      // Step 1: Upload audio file
      info('Uploading audio file...');
      const uploadResponse = await AIService.uploadVoiceAudio(audioBlob, {
        sessionName: `Voice Recording ${new Date().toLocaleString()}`,
        language: selectedLanguage,
        contractType: selectedContractType
      });

      if (!uploadResponse.success) {
        throw new Error('Failed to upload audio file');
      }

      const sessionId = uploadResponse.data.sessionId;
      setCurrentSessionId(sessionId);
      success('Audio uploaded successfully. Transcribing...');

      // Step 2: Transcribe audio
      const transcribeResponse = await AIService.transcribeVoiceAudio(sessionId);
      
      if (!transcribeResponse.success) {
        throw new Error('Failed to transcribe audio');
      }

      setTranscriptionText(transcribeResponse.data.transcription);
      success('Transcription completed. Generating contract...');

      // Step 3: Generate contract from transcription
      const contractResponse = await AIService.generateContractFromVoice(sessionId, {
        contractType: selectedContractType,
        language: selectedLanguage,
        jurisdiction: 'United States'
      });

      if (!contractResponse.success) {
        throw new Error('Failed to generate contract');
      }

      setGeneratedContract(contractResponse.data.contractContent);
      success('Contract generated successfully!');

      // Refresh sessions list
      await fetchVoiceSessions();
      
    } catch (error) {
      console.error('Error processing audio:', error);
      showError(error.message || 'Failed to process audio. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handler functions
  const handlePlay = (session) => {
    //console.log('Playing session:', session.sessionName);
    setIsPlaying(!isPlaying);
  };

  const handleView = (session) => {
    //console.log('Viewing session:', session.sessionName);
  };

  const handleDelete = (session) => {
    setSelectedItem(session);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    
    setIsDeleting(true);
    try {
      const sessionId = selectedItem.id || selectedItem.sessionId;
      await AIService.deleteVoiceSession(sessionId);
      
      setVoiceSessions(prev => prev.filter(session => 
        (session.id !== sessionId) && (session.sessionId !== sessionId)
      ));
      setShowDeleteModal(false);
      setSelectedItem(null);
      success('Voice session deleted successfully');
    } catch (error) {
      console.error('Error deleting session:', error);
      showError('Failed to delete voice session. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateContract = async () => {
    if (!transcriptionText.trim()) {
      showError('Please provide transcription text or record audio first');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await AIService.generateContractFromVoice(currentSessionId, {
        transcription: transcriptionText,
        contractType: selectedContractType,
        language: selectedLanguage,
        jurisdiction: 'United States'
      });
      
      if (response.success) {
        setGeneratedContract(response.data.contractContent);
        success('Contract generated successfully!');
        // Refresh sessions
        await fetchVoiceSessions();
      } else {
        throw new Error('Failed to generate contract');
      }
    } catch (error) {
      console.error('Error generating contract:', error);
      showError(error.message || 'Failed to generate contract. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Fetch sessions on mount
  useEffect(() => {
    fetchVoiceSessions();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Stop media stream if recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Voice-to-Contract"
        description="AI-powered voice processing, speech-to-text conversion, and intelligent contract generation"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Tabbed Navigation */}
      <div className="project-tabs">
        <div className="project-tabs-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <div className="project-tab-icon">
                {tab.id === 'record' && <Mic size={18} />}
                {tab.id === 'transcription' && <FileText size={18} />}
                {tab.id === 'contracts' && <FileCheck size={18} />}
                {tab.id === 'sessions' && <Clock size={18} />}
                {tab.id === 'analytics' && <BarChart3 size={18} />}
              </div>
              <div className="project-tab-content">
                <div className="project-tab-label">{tab.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-kpi-card color-1">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.totalSessions}</div>
                <div className="page-kpi-label">Total Sessions</div>
              </div>
              <div className="page-kpi-icon-container">
                <Mic size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-2">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.completedSessions}</div>
                <div className="page-kpi-label">Completed</div>
              </div>
              <div className="page-kpi-icon-container">
                <CheckCircle size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-3">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{Math.round(stats.averageAccuracy)}%</div>
                <div className="page-kpi-label">Avg. Accuracy</div>
              </div>
              <div className="page-kpi-icon-container">
                <Target size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>

        <div className="page-kpi-card color-3">
          <div className="page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <div className="page-kpi-value">{stats.contractsGenerated}</div>
                <div className="page-kpi-label">Contracts Generated</div>
              </div>
              <div className="page-kpi-icon-container">
                <FileCheck size={20} className="page-kpi-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Recording Tab */}
      {activeTab === 'record' && (
        <div>
          {/* Recording Interface */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 24px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <Mic size={28} style={{ color: 'var(--primary-color)' }} />
              Voice Recording
            </h3>

            {/* Recording Controls */}
            <div style={{ marginBottom: '32px' }}>
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '0',
                  backgroundColor: isRecording ? '#ef4444' : 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  fontSize: '48px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                {isRecording ? <Square size={40} /> : <Mic size={40} />}
              </button>
            </div>

            {/* Recording Status */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: isRecording ? '#ef4444' : '#6b7280',
                marginBottom: '8px'
              }}>
                {isRecording ? 'Recording...' : 'Ready to Record'}
              </div>
              <div style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#1f2937',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace'
              }}>
                {formatDuration(recordingDuration)}
              </div>
            </div>

            {/* Audio Level Indicator */}
            {isRecording && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  width: '200px',
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '0',
                  margin: '0 auto',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(audioLevel / 255) * 100}%`,
                    height: '100%',
                    backgroundColor: 'var(--primary-color)',
                    transition: 'width 0.1s ease'
                  }} />
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px'
                }}>
                  Audio Level
                </div>
              </div>
            )}

            {/* Processing Status */}
            {isProcessing && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '0',
                border: '1px solid var(--primary-color)20'
              }}>
                <RefreshCw size={20} style={{ color: 'var(--primary-color)', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '16px', color: 'var(--primary-color)', fontWeight: '500' }}>
                  Processing audio and generating contract...
                </span>
              </div>
            )}
          </div>

          {/* Recording Settings */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Recording Settings
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Language
                </label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="en-US">English (US)</option>
                  <option value="en-GB">English (UK)</option>
                  <option value="es-ES">Spanish</option>
                  <option value="fr-FR">French</option>
                  <option value="de-DE">German</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Contract Type
                </label>
                <select
                  value={selectedContractType}
                  onChange={(e) => setSelectedContractType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  <option value="service-agreement">Service Level Agreement</option>
                  <option value="software-license">Software License Agreement</option>
                  <option value="purchase-agreement">Purchase Agreement</option>
                  <option value="employment-contract">Employment Contract</option>
                  <option value="nda">Non-Disclosure Agreement</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transcription Tab */}
      {activeTab === 'transcription' && (
        <div>
          {/* Transcription Interface */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileText size={20} style={{ color: 'var(--primary-color)' }} />
              Transcription
            </h3>
            
            <textarea
              value={transcriptionText}
              onChange={(e) => setTranscriptionText(e.target.value)}
              placeholder="Transcribed text will appear here... You can also edit the transcription manually."
              rows={8}
              style={{
                width: '100%',
                padding: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '0',
                fontSize: '14px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                resize: 'vertical',
                lineHeight: '1.5'
              }}
            />
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '16px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {transcriptionText.length} characters
              </div>
              <button
                onClick={handleGenerateContract}
                disabled={!transcriptionText.trim() || isProcessing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  backgroundColor: !transcriptionText.trim() || isProcessing ? '#9ca3af' : 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: !transcriptionText.trim() || isProcessing ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                {isProcessing ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Brain size={16} />
                    Generate Contract
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Contracts Tab */}
      {activeTab === 'contracts' && (
        <div>
          {/* Contract Display */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileCheck size={20} style={{ color: 'var(--primary-color)' }} />
              Generated Contract
            </h3>
            
            {generatedContract ? (
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: '0',
                padding: '20px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151',
                maxHeight: '500px',
                overflowY: 'auto'
              }}>
                {generatedContract}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#6b7280',
                fontSize: '16px'
              }}>
                No contract generated yet. Record a voice session or provide transcription text.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session History Tab */}
      {activeTab === 'sessions' && (
        <div>
          {sessionsLoading ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid var(--primary-color)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#6b7280',
                margin: 0
              }}>
                Loading sessions...
              </h4>
            </div>
          ) : (
            <div>
              {/* Sessions DataTable */}
              <div className="page-table-container">
                <DataTable
                  data={filteredSessions}
                  columns={columns}
                  title="Voice-to-Contract Sessions"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={true}
                  showCheckboxes={false}
                  onView={handleView}
                  onEdit={handlePlay}
                  onDelete={handleDelete}
                  searchPlaceholder="Search voice sessions..."
                  emptyMessage="No voice sessions found. Start recording to create new sessions."
                  initialHiddenColumns={['id']}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0',
          padding: '20px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          color: '#6b7280'
        }}>
          Voice-to-Contract Analytics Dashboard Coming Soon!
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Voice Session"
        message={`Are you sure you want to delete "${selectedItem?.sessionName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
      <ToastContainer />
    </>
  );
};

export default VoiceToContract;
